// code-guardian-ignore-duplicates: the state-master fetch here deliberately mirrors DashboardPage's; the Dashboard keeps its own copy because it also needs the raw state rows (regionStatesParam) and logs failures, and the shared region logic already lives in utils/stateRegion.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { locationsAPI } from '../services/api';
import { buildRegionByStateKey, regionForStateName } from '../utils/stateRegion';

/**
 * Region lookup for the report pages' Region filter.
 *
 * We hold no region column of our own: the 1Office state master is the only
 * authority (each state row carries `regionName`), so it is fetched once and each
 * store's state is resolved client-side. This is the SAME master + the same two
 * helpers the Dashboard's Region filter uses, so a store lands in the same region
 * on a report as it does on the dashboard.
 *
 * Fails SOFT: if the master call fails the map stays empty, `regionOf` returns
 * undefined and `regionsFrom` offers no options — so every row passes a filter that
 * can't be set, rather than a lookup hiccup silently emptying a report.
 */
export function useStoreRegions(): {
  /** Region for a (possibly dirty) stored state string; undefined when unknown. */
  regionOf: (state?: string | null) => string | undefined;
  /** The regions actually present in the given states, sorted — dropdown options. */
  regionsFrom: (states: Array<string | null | undefined>) => string[];
} {
  const [states, setStates] = useState<Array<{ stateName?: string; regionName?: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    locationsAPI
      .getStates()
      .then((res) => {
        const rows = res.data?.status === 'success' && Array.isArray(res.data.data) ? res.data.data : [];
        if (!cancelled) setStates(rows);
      })
      .catch(() => { /* soft-fail: no master → the Region filter simply offers nothing */ });
    return () => { cancelled = true; };
  }, []);

  const byStateKey = useMemo(() => buildRegionByStateKey(states), [states]);

  const regionOf = useCallback(
    (state?: string | null) => regionForStateName(byStateKey, state),
    [byStateKey],
  );

  // Only regions the data actually contains, mirroring how every other report filter
  // builds its options — the dropdown never offers a region with no rows behind it.
  const regionsFrom = useCallback(
    (list: Array<string | null | undefined>) => {
      const present = new Set<string>();
      for (const s of list) {
        const r = regionOf(s);
        if (r) present.add(r);
      }
      return [...present].sort();
    },
    [regionOf],
  );

  return { regionOf, regionsFrom };
}
